import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TalonFieldMapping.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Tabs come from the registry so a new input page appears here without a
// frontend change. Pages TALON reaches no part of are listed too, so the gaps
// can be taken back to them.
const FALLBACK_DOMAINS = [{ key: 'deals', label: 'F&I Input' }];

// Values arrive as JSON, so dates are strings and numbers keep full precision.
function formatValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const asDate = /^\d{4}-\d{2}-\d{2}T/.test(value) ? new Date(value) : null;
    if (asDate && !Number.isNaN(asDate.getTime())) return asDate.toLocaleDateString();
    return value;
  }
  return JSON.stringify(value);
}

export default function TalonFieldMapping({ owners }) {
  const [dealershipId, setDealershipId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domain, setDomain] = useState('deals');

  // Working copy of the switches, saved only when the admin clicks Save.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Live samples, keyed by domain: { values: { [fieldId]: { raw, mapped } } }
  const [samples, setSamples] = useState({});
  const [sampling, setSampling] = useState(false);

  const domains = useMemo(() => {
    const labels = data?.domainLabels;
    if (!labels) return FALLBACK_DOMAINS;
    return Object.entries(labels).map(([key, label]) => ({ key, label }));
  }, [data]);

  // Only these pull records from TALON, so only they can show a live sample.
  const sourced = useMemo(
    () => new Set(data?.sourcedDomains || ['deals', 'inventory', 'service']),
    [data]
  );

  // Every dealership across all owners, so the picker is one flat list.
  const dealerships = useMemo(() => {
    const list = [];
    (owners || []).forEach(owner => {
      (owner.dealerships || []).forEach(d => list.push({ ...d, ownerName: owner.name }));
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [owners]);

  const loadFieldMap = useCallback(async (id) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/talon/field-map/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Could not load the field map');

      setData(json);
      const initial = {};
      json.fields.forEach(f => { initial[f.id] = f.enabled; });
      setDraft(initial);
      setSamples({});
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dealershipId) loadFieldMap(dealershipId);
  }, [dealershipId, loadFieldMap]);

  const fields = data?.fields || [];
  // Inputs the sync does not fill sit in the same sections as the ones it does,
  // so the page reads as the whole form rather than only the synced part.
  const groups = useMemo(() => {
    const order = [...(data?.domainGroups?.[domain] || [])];
    const byGroup = {};
    const add = row => {
      if (!byGroup[row.group]) {
        byGroup[row.group] = [];
        if (!order.includes(row.group)) order.push(row.group);
      }
      byGroup[row.group].push(row);
    };
    (data?.fields || []).filter(f => f.domain === domain).forEach(f => add({ ...f, synced: true }));
    (data?.gaps || []).filter(g => g.domain === domain).forEach(g => add({ ...g, synced: false }));
    // Rows carry the position of their input on the form; anything without one
    // keeps registry order behind those that do.
    const byOrder = (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity);
    return order
      .filter(name => byGroup[name])
      .map(name => ({ name, fields: byGroup[name].slice().sort(byOrder) }));
  }, [data, domain]);

  const GAP_LABEL = {
    derived: 'Calculated by Masters Path',
    unmapped: 'In TALON, not yet mapped',
    unavailable: 'Not available in TALON',
  };

  // Counts describe the form: how many of a page's inputs the sync fills, and what
  // the rest are waiting on. Reporting Only rows are left out of the input count —
  // they are the feed the page is built from, not boxes anyone types in.
  const statsFor = useCallback(
    domainKey => {
      const onForm = row => row.group !== 'Reporting Only';
      const mine = row => row.domain === domainKey;
      const mapped = fields.filter(f => mine(f) && onForm(f));
      const gaps = (data?.gaps || []).filter(g => mine(g) && onForm(g));
      const kind = k => gaps.filter(g => g.kind === k).length;

      const active = mapped.filter(f => draft[f.id]).length;
      const derived = kind('derived');

      return {
        inputs: mapped.length + gaps.length,
        // A field TALON fills directly plus one the sync works out for itself.
        filled: active + derived,
        active,
        derived,
        off: mapped.length - active,
        unmapped: kind('unmapped'),
        unavailable: kind('unavailable'),
        supporting:
          fields.filter(f => mine(f) && !onForm(f)).length +
          (data?.gaps || []).filter(g => mine(g) && !onForm(g)).length,
      };
    },
    [fields, data, draft]
  );

  const stats = useMemo(() => statsFor(domain), [statsFor, domain]);

  const dirty = fields.some(f => draft[f.id] !== f.enabled);

  const setField = (id, value) => {
    setDraft(prev => ({ ...prev, [id]: value }));
    setMessage('');
  };

  const setGroup = (groupFields, value) => {
    setDraft(prev => {
      const next = { ...prev };
      groupFields.forEach(f => { if (!f.required) next[f.id] = value; });
      return next;
    });
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      // Required fields always sync, so they are not part of the saved switches.
      const overrides = {};
      fields.forEach(f => { if (!f.required) overrides[f.id] = !!draft[f.id]; });

      const response = await fetch(`${API_URL}/api/admin/talon/field-map/${dealershipId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ overrides }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Could not save');

      await loadFieldMap(dealershipId);
      setMessage('Saved. The next sync will use these settings.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const initial = {};
    fields.forEach(f => { initial[f.id] = f.enabled; });
    setDraft(initial);
    setMessage('');
  };

  const handleFetchSample = async () => {
    setSampling(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/talon/field-map/${dealershipId}/sample/${domain}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || json.error || 'Could not reach TALON');

      if (!json.found) {
        setMessage(json.message || 'TALON returned no records for this domain.');
        return;
      }

      const byId = {};
      json.values.forEach(v => { byId[v.id] = v; });
      setSamples(prev => ({ ...prev, [domain]: byId }));
      setMessage('Showing a live record from TALON.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSampling(false);
    }
  };

  const sample = samples[domain];

  return (
    <div className="tfm">
      <p className="tfm-intro">
        Everything the TALON sync writes, grouped by the page it feeds, with the TALON field
        behind it and an example of the value that lands there. Switch a field off to leave it
        out of the sync entirely, so it stays hand-entered.
      </p>

      <div className="tfm-toolbar">
        <select
          className="tfm-select"
          value={dealershipId}
          onChange={e => setDealershipId(e.target.value)}
        >
          <option value="">Select a dealership…</option>
          {dealerships.map(d => (
            <option key={d.id} value={d.id}>
              {d.name}{d.ownerName ? ` — ${d.ownerName}` : ''}
            </option>
          ))}
        </select>

        {data && (
          <div className="tfm-status">
            <span className={`tfm-pill ${data.dealership.talonEnabled ? 'ok' : 'warn'}`}>
              {data.dealership.talonEnabled ? 'TALON enabled' : 'TALON add-on off'}
            </span>
            <span className={`tfm-pill ${data.dealership.hasDatasetKey ? 'ok' : 'warn'}`}>
              {data.dealership.hasDatasetKey ? 'Dataset key set' : 'No dataset key'}
            </span>
            <span className={`tfm-pill ${data.hasSyncedData ? 'ok' : ''}`}>
              {data.hasSyncedData ? 'Synced data available' : 'No synced records yet'}
            </span>
          </div>
        )}
      </div>

      {loading && <div className="tfm-empty">Loading field map…</div>}
      {error && <div className="tfm-msg error" style={{ marginBottom: '14px' }}>{error}</div>}

      {!dealershipId && !loading && (
        <div className="tfm-empty">Choose a dealership to review its TALON field mapping.</div>
      )}

      {data && !loading && (
        <>
          <div className="tfm-domains">
            {domains.map(d => {
              const s = statsFor(d.key);
              return (
                <button
                  key={d.key}
                  className={domain === d.key ? 'active' : ''}
                  onClick={() => setDomain(d.key)}
                  title={`The sync fills ${s.filled} of this page's ${s.inputs} inputs`}
                >
                  {d.label}
                  <span className="tfm-count">{`${s.filled}/${s.inputs}`}</span>
                </button>
              );
            })}
          </div>

          {data.domainNotes?.[domain] && (
            <p className="tfm-domain-note">{data.domainNotes[domain]}</p>
          )}

          <div className="tfm-tally">
            <div className="tfm-tally-item">
              <span className="tfm-tally-n">{stats.inputs}</span>
              <span className="tfm-tally-label">Inputs on this page</span>
            </div>
            <div className="tfm-tally-item">
              <span className="tfm-tally-n ok">{stats.filled}</span>
              <span className="tfm-tally-label">
                Filled by the sync
                {stats.derived > 0 && (
                  <em>
                    {stats.active} from TALON fields, {stats.derived} worked out by Masters Path
                  </em>
                )}
              </span>
            </div>
            {stats.off > 0 && (
              <div className="tfm-tally-item">
                <span className="tfm-tally-n warn">{stats.off}</span>
                <span className="tfm-tally-label">
                  Switched off
                  <em>TALON can fill these, but this dealership types them in</em>
                </span>
              </div>
            )}
            <div className="tfm-tally-item">
              <span className="tfm-tally-n warn">{stats.unmapped}</span>
              <span className="tfm-tally-label">
                In TALON, not yet mapped
                <em>Pending field verification</em>
              </span>
            </div>
            <div className="tfm-tally-item">
              <span className="tfm-tally-n">{stats.unavailable}</span>
              <span className="tfm-tally-label">
                Not in TALON
                <em>Hand-entered until TALON publishes them</em>
              </span>
            </div>
            <div className="tfm-tally-item">
              <span className="tfm-tally-n">{stats.supporting}</span>
              <span className="tfm-tally-label">
                Supporting fields captured
                <em>Stored for reporting, not shown on the form</em>
              </span>
            </div>
          </div>

          <div className="tfm-actions">
            <button
              className="tfm-btn primary"
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="tfm-btn" onClick={handleReset} disabled={!dirty || saving}>
              Reset
            </button>
            <button
              className="tfm-btn"
              onClick={handleFetchSample}
              disabled={
                sampling || !sourced.has(domain) ||
                !data.dealership.talonEnabled || !data.dealership.hasDatasetKey
              }
              title={
                !sourced.has(domain)
                  ? 'TALON publishes no records behind this page'
                  : data.dealership.talonEnabled && data.dealership.hasDatasetKey
                    ? 'Pull one live record from TALON'
                    : 'Needs the TALON add-on enabled and a dataset key'
              }
            >
              {sampling ? 'Fetching…' : 'Fetch live sample'}
            </button>
            {dirty && <span className="tfm-dirty">Unsaved changes</span>}
            {message && !dirty && <span className="tfm-msg">{message}</span>}
          </div>

          {groups.map(group => {
            const switchable = group.fields.filter(f => f.synced && !f.required);
            const allOn = switchable.length > 0 && switchable.every(f => draft[f.id]);
            return (
              <div className="tfm-group" key={group.name}>
                <div className="tfm-group-head">
                  <span className="tfm-group-name">{group.name}</span>
                  {switchable.length > 0 && (
                    <button
                      className="tfm-group-toggle"
                      onClick={() => setGroup(switchable, !allOn)}
                    >
                      {allOn ? 'Turn all off' : 'Turn all on'}
                    </button>
                  )}
                </div>

                <div className="tfm-table-wrap">
                <table className="tfm-table">
                  <thead>
                    <tr>
                      <th className="tfm-col-switch" />
                      <th className="tfm-col-input">Masters Path field</th>
                      <th className="tfm-col-source">TALON field</th>
                      <th>How it is handled</th>
                      <th className="tfm-col-value">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.fields.map(f => {
                      if (!f.synced) {
                        return (
                          <tr key={f.id} className="gap">
                            <td />
                            <td>
                              <span className="tfm-input-label">{f.label}</span>
                              <span className={`tfm-badge gap ${f.kind}`}>{GAP_LABEL[f.kind]}</span>
                            </td>
                            <td>
                              {f.sources.length === 0 ? (
                                <span className="tfm-source none">—</span>
                              ) : (
                                <span className="tfm-source">{f.sources.join('  +  ')}</span>
                              )}
                            </td>
                            <td className="tfm-notes">{f.notes}</td>
                            <td><span className="tfm-value empty">hand-entered</span></td>
                          </tr>
                        );
                      }

                      const on = !!draft[f.id];
                      const live = sample?.[f.id];
                      const example = live
                        ? formatValue(live.mapped)
                        : formatValue(f.example);
                      const rawParts = live
                        ? Object.entries(live.raw)
                            .map(([k, v]) => `${k.split('.').pop()}: ${formatValue(v) ?? '—'}`)
                        : [];

                      return (
                        <tr key={f.id} className={on ? '' : 'off'}>
                          <td>
                            <input
                              type="checkbox"
                              checked={on}
                              disabled={f.required}
                              onChange={() => setField(f.id, !on)}
                              style={{ accentColor: '#10b981', cursor: f.required ? 'default' : 'pointer' }}
                              title={f.required ? 'Always synced — used to match records' : ''}
                            />
                          </td>
                          <td>
                            <span className="tfm-input-label">{f.label}</span>
                            {f.required && <span className="tfm-badge required">Always</span>}
                            {f.overridden && <span className="tfm-badge custom">Custom</span>}
                            <span className="tfm-column">{f.column}</span>
                          </td>
                          <td>
                            {f.sources.length === 0 ? (
                              <span className="tfm-source none">Written by the integration</span>
                            ) : (
                              <span className="tfm-source">{f.sources.join('  +  ')}</span>
                            )}
                          </td>
                          <td className="tfm-notes">{f.notes}</td>
                          <td>
                            {example === null ? (
                              <span className="tfm-value empty">no value</span>
                            ) : (
                              <span className="tfm-value">{example}</span>
                            )}
                            {live?.error && (
                              <span className="tfm-raw" style={{ color: 'rgba(239,68,68,0.8)' }}>
                                {live.error}
                              </span>
                            )}
                            {rawParts.length > 0 && (
                              <span className="tfm-raw">{rawParts.join(' · ')}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
